/**
 * Scoped GSAP for a component, with teardown that cannot be forgotten.
 *
 * ## The cleanup problem this exists to remove
 *
 * A tween outlives the component that created it. So does a ScrollTrigger — worse, it
 * stays registered against the scroller, keeps measuring an element that has been removed
 * from the document, and holds a reference to it. Ten navigations through the library and
 * the page is animating against ten screens that no longer exist. Nothing throws; the page
 * just gets slower, which is the failure mode that never gets reported and never gets
 * found.
 *
 * `gsap.context()` is the answer, and the reason every animation in this app goes through
 * this hook rather than touching `gsap` directly. Everything created inside the callback —
 * tweens, timelines, ScrollTriggers, event listeners GSAP registered on their behalf — is
 * recorded, and `revert()` undoes all of it in one call. There is no list to keep in step
 * with the code, so nothing can be left off it.
 *
 * ## Why not `@gsap/react`'s `useGSAP()`
 *
 * GreenSock's own React guidance prefers that hook, and it would be the right call in most
 * projects — it does the same `revert()` and adds `contextSafe` for callbacks. It is
 * declined here for one reason: it must be imported statically and registered at module
 * scope (`gsap.registerPlugin(useGSAP)`), which puts GSAP in the entry chunk of every
 * build. That is precisely what this layer exists to avoid, and the television would carry
 * an animation engine it is forbidden from running. The same guidance explicitly sanctions
 * the fallback taken here — `gsap.context()` inside an effect, with `revert()` in the
 * cleanup — so nothing is lost but the callback helper, which nothing in this app needs.
 *
 * ## Why the callback may run late
 *
 * GSAP is fetched on demand (see `runtime.ts`), so the setup runs a microtask or two after
 * mount rather than during the effect. That is invisible for entrance and scroll work,
 * which start on a later frame anyway, and it is what lets a device that never animates
 * avoid downloading the engine at all.
 */
import { useEffect, useRef, type DependencyList, type RefObject } from 'react';
import { useMotion } from './MotionContext';
import { loadMotionRuntime, type MotionRuntime } from './runtime';

export type GsapSetup = (runtime: MotionRuntime) => void;

/**
 * @param setup   Runs inside a `gsap.context`. Everything it creates is reverted on
 *                unmount, on a dependency change, and when motion is switched off.
 * @param deps    Re-runs the setup, reverting the previous pass first.
 * @param scope   Optional element ref. Selector strings inside `setup` are then resolved
 *                within it, so `'.card'` cannot reach into another screen's cards.
 */
export function useGsap(
  setup: GsapSetup,
  deps: DependencyList = [],
  scope?: RefObject<HTMLElement | null>,
): void {
  const { animate } = useMotion();

  // Held in a ref so a caller may write the setup inline without it counting as a
  // dependency — an inline arrow is a new function every render, and depending on it
  // would revert and rebuild every animation on every render.
  const setupRef = useRef(setup);
  setupRef.current = setup;

  useEffect(() => {
    if (!animate) return;

    let cancelled = false;
    let context: ReturnType<MotionRuntime['gsap']['context']> | null = null;

    void loadMotionRuntime().then((runtime) => {
      if (cancelled) return;
      context = runtime.gsap.context(() => setupRef.current(runtime), scope?.current ?? undefined);
    });

    return () => {
      cancelled = true;
      // Safe before the runtime arrives (`context` is still null) and safe to call on a
      // context whose elements have already been unmounted — GSAP checks.
      context?.revert();
      context = null;
    };
    // `scope` is a ref object and stable by construction; including it would add nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animate, ...deps]);
}
